import { generateClient } from 'aws-amplify/api';
import {
  getUserMovieInteractionQuery,
  createUserMovieInteractionMutation,
  updateUserMovieInteractionMutation
} from '../graphql/movieInteractions';
import {
  createWatchlistItemMutation,
  createWatchedItemMutation,
  deleteWatchlistItemMutation,
  deleteWatchedItemMutation,
  getUserWatchlistQuery,
  getUserWatchedQuery
} from '../graphql/watchlist';
import { achievementService } from './achievementService';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

export const getUserMovieInteraction = async (userID, movieID) => {
  try {
    const result = await getClient().graphql({
      query: getUserMovieInteractionQuery,
      variables: { userID, movieID }
    });
    
    if (result.errors && result.errors.length > 0) {
      console.error('Error fetching movie interaction:', result.errors);
      return null;
    }
    
    return result.data?.listUserMovieInteractions?.items?.[0] || null;
  } catch (error) {
    console.error('Error fetching movie interaction:', error);
    return null; // Return null instead of throwing to prevent breaking the UI
  }
};

// Legacy sync functions disabled to prevent errors
// These tables are deprecated in favor of UserMovieInteraction table

// Track interaction history for analytics
const trackInteractionHistory = async (userID, movieID, updates, interaction) => {
  try {
    // Only track significant state changes
    const significantUpdates = {};
    if (updates.hasOwnProperty('watched')) significantUpdates.watched = updates.watched;
    if (updates.hasOwnProperty('inWatchlist')) significantUpdates.inWatchlist = updates.inWatchlist;
    if (updates.hasOwnProperty('isFavorite')) significantUpdates.isFavorite = updates.isFavorite;
    if (updates.hasOwnProperty('rating')) significantUpdates.rating = updates.rating;
    
    if (Object.keys(significantUpdates).length > 0) {
      console.log('📊 Tracking interaction history:', { userID, movieID, updates: significantUpdates });
      // Could implement UserMovieInteractionHistory table here
      // await createInteractionHistoryRecord(userID, movieID, significantUpdates);
    }
  } catch (error) {
    console.error('⚠️ Error tracking interaction history:', error);
  }
};

// Sync function for legacy dedicated tables
const syncLegacyTables = async (userID, movieID, updates, interaction) => {
  try {
    // Handle WatchlistItem table sync
    if (updates.hasOwnProperty('inWatchlist')) {
      if (updates.inWatchlist) {
        // Check if already exists in WatchlistItem table first
        try {
          const existingResult = await getClient().graphql({
            query: getUserWatchlistQuery,
            variables: { userID }
          });
          const existingItems = existingResult.data?.listWatchlistItems?.items?.filter(
            item => item.movieID === movieID
          ) || [];
          
          if (existingItems.length > 0) {
            console.log('ℹ️ Movie already exists in WatchlistItem table - skipping duplicate creation');
            return;
          }
          
          // Try to find user's default watchlist for legacy sync
          const watchlistResult = await getClient().graphql({
            query: `
              query GetUserDefaultWatchlist($userID: ID!) {
                listUserWatchlists(
                  filter: { 
                    and: [
                      { userID: { eq: $userID } }
                      { isDefault: { eq: true } }
                    ]
                  }
                  limit: 1
                ) {
                  items {
                    id
                    name
                  }
                }
              }
            `,
            variables: { userID }
          });
          
          const defaultWatchlist = watchlistResult.data?.listUserWatchlists?.items?.[0];
          
          if (defaultWatchlist) {
            await getClient().graphql({
              query: createWatchlistItemMutation,
              variables: {
                input: {
                  userID,
                  watchlistID: defaultWatchlist.id,
                  movieID,
                  contentType: 'MOVIE',
                  status: 'WANT_TO_WATCH',
                  priority: 5
                }
              }
            });
            console.log('✅ Added to WatchlistItem table with default watchlist:', defaultWatchlist.name);
          } else {
            console.log('ℹ️ No default watchlist found - skipping WatchlistItem sync');
          }
        } catch (error) {
          // Don't fail main operation if legacy sync fails
          console.warn('⚠️ Error syncing with WatchlistItem table:', error.message);
        }
      } else {
        // Remove from watchlist - find and delete WatchlistItem entries
        try {
          const watchlistResult = await getClient().graphql({
            query: getUserWatchlistQuery,
            variables: { userID }
          });
          const watchlistItems = watchlistResult.data?.listWatchlistItems?.items?.filter(
            item => item.movieID === movieID
          ) || [];
          
          if (watchlistItems.length > 0) {
            for (const watchlistItem of watchlistItems) {
              await getClient().graphql({
                query: deleteWatchlistItemMutation,
                variables: { input: { id: watchlistItem.id } }
              });
            }
            console.log(`✅ Removed ${watchlistItems.length} WatchlistItem entries`);
          } else {
            console.log('ℹ️ No WatchlistItem entries found to remove');
          }
        } catch (error) {
          console.warn('⚠️ Error removing from WatchlistItem table:', error.message);
        }
      }
    }

    // Handle WatchedItem table sync
    if (updates.hasOwnProperty('watched')) {
      if (updates.watched) {
        // Check if already exists before adding
        try {
          const watchedResult = await getClient().graphql({
            query: getUserWatchedQuery,
            variables: { userID }
          });
          const existingWatchedItem = watchedResult.data?.listWatchedItems?.items?.find(
            item => item.movieID === movieID
          );
          
          if (!existingWatchedItem) {
            await getClient().graphql({
              query: createWatchedItemMutation,
              variables: {
                input: {
                  userID,
                  movieID,
                  status: 'WATCHED'
                }
              }
            });
            console.log('✅ Added to WatchedItem table');
          } else {
            console.log('ℹ️ Already exists in WatchedItem table');
          }
        } catch (error) {
          console.error('⚠️ Error adding to WatchedItem:', error);
        }
      } else {
        // Remove from watched - find all items and delete them
        // Always check for and remove any existing WatchedItem entries when unmarking as watched
        try {
          const watchedResult = await getClient().graphql({
            query: getUserWatchedQuery,
            variables: { userID }
          });
          const watchedItems = watchedResult.data?.listWatchedItems?.items?.filter(
            item => item.movieID === movieID
          ) || [];
          
          if (watchedItems.length > 0) {
            console.log(`🗑️ Found ${watchedItems.length} WatchedItem entries to remove for movie ${movieID}`);
            for (const watchedItem of watchedItems) {
              await getClient().graphql({
                query: deleteWatchedItemMutation,
                variables: { 
                  input: { 
                    id: watchedItem.id,
                    createdAt: watchedItem.createdAt
                  } 
                }
              });
              console.log('✅ Removed from WatchedItem table:', watchedItem.id);
            }
          } else {
            console.log('ℹ️ No WatchedItem entries found to remove for movie', movieID);
          }
        } catch (error) {
          console.error('⚠️ Error removing from WatchedItem:', error);
        }
      }
    }
  } catch (error) {
    console.error('⚠️ Error syncing legacy tables:', error);
    // Don't throw - legacy sync failures shouldn't break main functionality
  }
};

export const updateMovieInteraction = async (userID, movieID, updates) => {
  console.log('🎬 updateMovieInteraction called:', { userID, movieID, updates });
  try {
    let interaction = await getUserMovieInteraction(userID, movieID);
    let result;
    
    if (!interaction) {
      result = await getClient().graphql({
        query: createUserMovieInteractionMutation,
        variables: {
          input: { userID, movieID, ...updates }
        }
      });
      
      if (result.errors && result.errors.length > 0) {
        console.error('Error creating movie interaction:', result.errors);
        throw new Error(`GraphQL errors: ${result.errors.map(e => e.message).join(', ')}`);
      }
      
      interaction = result.data.createUserMovieInteraction;
      console.log('✅ Created new UserMovieInteraction:', {
        id: interaction.id,
        userID: interaction.userID,
        movieID: interaction.movieID,
        watched: interaction.watched,
        inWatchlist: interaction.inWatchlist
      });
    } else {
      console.log('📝 Updating existing interaction:', {
        id: interaction.id,
        currentState: {
          watched: interaction.watched,
          inWatchlist: interaction.inWatchlist
        },
        updates
      });
      
      result = await getClient().graphql({
        query: updateUserMovieInteractionMutation,
        variables: {
          input: { id: interaction.id, ...updates }
        }
      });
      
      if (result.errors && result.errors.length > 0) {
        console.error('Error updating movie interaction:', result.errors);
        throw new Error(`GraphQL errors: ${result.errors.map(e => e.message).join(', ')}`);
      }
      
      interaction = result.data.updateUserMovieInteraction;
      console.log('✅ Updated UserMovieInteraction:', {
        id: interaction.id,
        userID: interaction.userID,
        movieID: interaction.movieID,
        watched: interaction.watched,
        inWatchlist: interaction.inWatchlist
      });
    }
    
    // Sync with legacy dedicated tables for backward compatibility
    await syncLegacyTables(userID, movieID, updates, interaction);
    
    // Track interaction history for analytics (optional)
    await trackInteractionHistory(userID, movieID, updates, interaction);
    
    // Trigger achievement calculation for significant actions
    if (updates.watched === true) {
      try {
        await achievementService.calculateProgress(userID, 'mark_watched', { movieID });
      } catch (error) {
        console.error('Error triggering achievement calculation:', error);
      }
    }
    
    if (updates.inWatchlist === true) {
      try {
        await achievementService.calculateProgress(userID, 'add_to_watchlist', { movieID });
      } catch (error) {
        console.error('Error triggering achievement calculation:', error);
      }
    }
    
    console.log('✅ updateMovieInteraction completed successfully:', interaction);
    return interaction;
  } catch (error) {
    console.error('❌ Error updating movie interaction:', error);
    throw error;
  }
};

export const markAsWatched = async (userID, movieID) => {
  console.log('👁️ markAsWatched called:', { userID, movieID });
  return updateMovieInteraction(userID, movieID, { 
    watched: true
  });
};

export const unmarkAsWatched = async (userID, movieID) => {
  console.log('🙈 unmarkAsWatched called:', { userID, movieID });
  return updateMovieInteraction(userID, movieID, { 
    watched: false 
  });
};

export const markAsNotInterested = async (userID, movieID) => {
  console.log('🙅 markAsNotInterested called:', { userID, movieID });
  return updateMovieInteraction(userID, movieID, { 
    notInterested: true,
    inWatchlist: false 
  });
};

export const addToWatchlist = async (userID, movieID) => {
  console.log('📥 addToWatchlist called:', { userID, movieID });
  return updateMovieInteraction(userID, movieID, { 
    inWatchlist: true,
    notInterested: false 
  });
};

export const removeFromWatchlist = async (userID, movieID) => {
  console.log('📤 removeFromWatchlist called:', { userID, movieID });
  return updateMovieInteraction(userID, movieID, { 
    inWatchlist: false 
  });
};

export const toggleFavorite = async (userID, movieID) => {
  const interaction = await getUserMovieInteraction(userID, movieID);
  const isFavorite = interaction?.isFavorite || false;
  return updateMovieInteraction(userID, movieID, { 
    isFavorite: !isFavorite 
  });
};

export const incrementWatchCount = async (userID, movieID) => {
  const interaction = await getUserMovieInteraction(userID, movieID);
  const now = new Date().toISOString();
  
  // Check if last watch was more than 5 hours ago
  if (interaction?.lastWatchedAt) {
    const lastWatch = new Date(interaction.lastWatchedAt);
    const fiveHoursAgo = new Date(Date.now() - 5 * 60 * 60 * 1000);
    
    if (lastWatch > fiveHoursAgo) {
      throw new Error('Must wait 5 hours between watch counts');
    }
  }
  
  const currentCount = interaction?.watchCount || 0;
  const today = new Date().toDateString();
  const lastWatchDate = interaction?.lastWatchedAt ? 
    new Date(interaction.lastWatchedAt).toDateString() : null;
  
  // Check daily limit (max 2 per day)
  if (lastWatchDate === today && currentCount >= 2) {
    throw new Error('Daily watch count limit reached');
  }
  
  return updateMovieInteraction(userID, movieID, {
    watchCount: currentCount + 1,
    lastWatchedAt: now
  });
};